// src/app/core/auth.service.ts
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, Observable, Subject, firstValueFrom, from, merge, of, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { normalizeAppRole, normalizeAppRoles } from '../core/role-utils';
import { TenantContextService } from '../core/tenant-context.service';
import { PrivilegedClaimsService } from '../core/privileged-claims.service';
import { workspaceHomeForShell, workspaceShellForRoles } from '../core/workspace-role';
import { environment } from '../../environments/environment';

type SessionClaims = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class AuthService {

  
  // ---- public reactive state
  user$ = this.afAuth.authState.pipe(shareReplay(1));
  /**
   * Re-emits claims after a callable changes custom claims. Firebase's
   * authState stream does not reliably emit for an ID-token refresh, so
   * relying on user$ alone leaves MFA guards with stale mfaPassedAt.
   */
  private claimsRefresh$ = new Subject<void>();

  idTokenResult$ = merge(
    this.user$.pipe(
      switchMap(u => u ? from(u.getIdTokenResult(true)) : of(null))
    ),
    this.claimsRefresh$.pipe(
      switchMap(() => from(this.afAuth.currentUser)),
      switchMap(u => u ? from(u.getIdTokenResult(true)) : of(null))
    )
  ).pipe(shareReplay(1));
  claims$ = this.idTokenResult$.pipe(
    map(r => (r?.claims as SessionClaims) || {}),
    shareReplay(1)
  );
  // Operational roles come from the normalized Firestore profile only.
  // Token claims remain available for MFA/privileged boundaries, not shell routing.
  roles$ = this.tenantContext.context$.pipe(
    map((context) => normalizeAppRoles([
      ...(context.roles || []),
      ...(normalizeAppRole(context.role) ? [context.role as string] : []),
    ])),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    shareReplay(1)
  );
  workspaceHome$ = this.roles$.pipe(
    map((roles) => this.getWorkspaceHomeUrl(roles)),
    distinctUntilChanged(),
    shareReplay(1)
  );
  workspaceLabel$ = this.roles$.pipe(
    map((roles) => this.getWorkspaceLabel(roles)),
    distinctUntilChanged(),
    shareReplay(1)
  );

  // Emits true once we have *some* idTokenResult (means “safe to read rules that depend on roles”)
  ready$ = this.idTokenResult$.pipe(map(r => !!r), shareReplay(1));

  /** MFA gate state derived from custom claims (see functions/src/mfa.ts
   *  and auth-events.ts for how these are set/cleared). mfaPassedAt is
   *  cleared on every fresh sign-in server-side, so its presence here
   *  reflects "verified THIS session," not "ever enrolled." */
  mfaStatus$ = this.claims$.pipe(
    map(c => ({
      requireMfa: c['requireMfa'] === true,
      mfaEnrolled: c['mfaEnrolled'] === true,
      mfaPassed: typeof c['mfaPassedAt'] === 'number' && c['mfaPassedAt'] > 0,
      /** Durable -- true once the account has ever used its one-time
       *  enrollment skip. Controls whether "Not now" is offered. */
      mfaGraceUsed: c['mfaGraceUsed'] === true,
      /** Session-scoped, cleared every sign-in like mfaPassed -- true
       *  only for the login where "Not now" was just clicked. */
      mfaGraceGranted: typeof c['mfaGraceGrantedAt'] === 'number' && c['mfaGraceGrantedAt'] > 0
    })),
    shareReplay(1)
  );

  // Legacy flags you use elsewhere
  private loggedIn = new BehaviorSubject<boolean>(!!localStorage.getItem('token'));
  isLoggedIn$: Observable<boolean> = this.loggedIn.asObservable();
  private userName = new BehaviorSubject<string | null>(localStorage.getItem('userName'));
  userName$: Observable<string | null> = this.userName.asObservable();

  constructor(
    private afAuth: AngularFireAuth,
    private router: Router,
    private afs: AngularFirestore,
    private tenantContext: TenantContextService,
    private functions: AngularFireFunctions,
    private privilegedClaims: PrivilegedClaimsService
  ) {}

  // ---- utilities
  async ensureFreshToken(): Promise<void> {
    const u = firebase.auth().currentUser;
    if (u) {
      await u.getIdToken(true);
      // Notify claims$ / mfaStatus$ that the refreshed claims are ready.
      this.claimsRefresh$.next();
    }
  }

  /**
   * Keep the signed Firebase token aligned with the operational profile.
   *
   * Firestore/Storage rules intentionally authorize clinical writes from
   * signed custom claims, while the shell reads the Firestore profile. Older
   * accounts can therefore look like a Nurse/NP in the UI but still carry a
   * token with no role or tenant. The callable is actor-bound and refuses
   * super-admin self elevation; this client only invokes it when the current
   * non-platform profile and token are actually out of sync.
   */
  async syncClinicalClaimsIfNeeded(): Promise<boolean> {
    const user = await this.afAuth.currentUser;
    if (!user) return false;

    const context = await firstValueFrom(this.tenantContext.context$.pipe(take(1)));
    const profileRoles = normalizeAppRoles([
      ...(context.roles || []),
      ...(normalizeAppRole(context.role) ? [context.role as string] : []),
    ]).filter((role) => role !== 'super_admin');

    // Claim-only platform identities are deliberately outside this repair.
    if (!profileRoles.length || !context.orgId) return false;

    if (await this.privilegedClaims.matchesOperationalIdentity(profileRoles, context.orgId)) return false;

    await firstValueFrom(this.functions.httpsCallable('syncMyClaimsV1')({}));
    await this.ensureFreshToken();
    return true;
  }

  /** Wait until the normalized profile exposes the requested role. */
  async waitForRole(role: string, timeoutMs = 6000, intervalMs = 300): Promise<boolean> {
    const target = normalizeAppRole(role);
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const context = await firstValueFrom(this.tenantContext.context$.pipe(take(1)));
      const roles = normalizeAppRoles([
        ...(context.roles || []),
        ...(normalizeAppRole(context.role) ? [context.role as string] : []),
      ]);
      if (target && roles.includes(target)) return true;
      await new Promise(r => setTimeout(r, intervalMs));
    }
    return false;
  }

  async waitForAnyRole(timeoutMs = 6000, intervalMs = 300): Promise<string[]> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const context = await firstValueFrom(this.tenantContext.context$.pipe(take(1)));
      const roles = normalizeAppRoles([
        ...(context.roles || []),
        ...(normalizeAppRole(context.role) ? [context.role as string] : []),
      ]);
      if (roles.length) return roles;
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    return [];
  }

  hasRole(role: string): Observable<boolean> {
    return this.roles$.pipe(map(roles => roles.includes(role)));
  }

  getWorkspaceHomeUrl(roles: string[]): string {
    if (normalizeAppRoles(roles).some(role=>['billing_partner','credentialing_partner','supply_partner'].includes(role))) return '/partner';
    if (
      normalizeAppRoles(roles).includes('patient') &&
      ['jade-dev-5b0e3', 'woundapp-261e6'].includes(environment.firebase.projectId)
    ) {
      return '/telehealth-dev/patient-portal';
    }
    return workspaceHomeForShell(workspaceShellForRoles(roles));
  }

  getWorkspaceLabel(roles: string[]): string {
    if (normalizeAppRoles(roles).some(role=>['billing_partner','credentialing_partner','supply_partner'].includes(role))) return 'Partner Dashboard';
    switch (workspaceShellForRoles(roles)) {
      case 'super_admin': return 'Platform Command Center';
      case 'admin': return 'Admin Dashboard';
      case 'provider': return 'Provider Dashboard';
      case 'nurse': return 'Nurse Dashboard';
      case 'frontdesk': return 'Reception Dashboard';
      default: return 'Main Portal';
    }
  }

  // ---- auth flows
  async signInWithEmailAndPassword(email: string, password: string) {
    const cred = await this.afAuth.signInWithEmailAndPassword(email, password);

    // Force fresh claims immediately after sign-in
    await this.ensureFreshToken();

    // Wait for the normalized user profile before role-based redirects occur.
    await this.waitForAnyRole();
    await this.syncClinicalClaimsIfNeeded();

    // UX flags
    this.loggedIn.next(true);
    localStorage.setItem('token', 'true');

    const displayName = cred.user?.displayName || email.split('@')[0];
    this.userName.next(displayName);
    localStorage.setItem('userName', displayName);

    if (cred.user?.uid) {
      await this.updateLastLogin(cred.user.uid);
    }
    return cred;
  }

  /** Legacy method kept for compatibility */
  login(email: string, password: string) {
    this.afAuth.signInWithEmailAndPassword(email, password).then(
      async (userCredential) => {
        await this.ensureFreshToken();
        const roles = await this.waitForAnyRole();
        await this.syncClinicalClaimsIfNeeded();

        this.loggedIn.next(true);
        localStorage.setItem('token', 'true');

        const user = userCredential.user;
        if (user) {
          const displayName = user.displayName || email.split('@')[0];
          this.userName.next(displayName);
          localStorage.setItem('userName', displayName);
          await this.updateLastLogin(user.uid);
        }

        await this.router.navigateByUrl(this.getWorkspaceHomeUrl(roles));
      },
      (err) => {
        alert(err.message);
        this.router.navigate(['/login']);
      }
    );
  }

  async getIdToken(forceRefresh = true): Promise<string | null> {
    const user = await this.afAuth.currentUser;
    return user ? user.getIdToken(forceRefresh) : null;
  }

  register(email: string, password: string, displayName: string) {
    this.afAuth.createUserWithEmailAndPassword(email, password).then(async (uc) => {
      if (uc.user) {
        await uc.user.updateProfile({ displayName });
        await this.ensureFreshToken();
        alert('Registration successful');
        this.userName.next(displayName);
        localStorage.setItem('userName', displayName);
        this.router.navigate(['/login']);
      }
    }, (err) => {
      alert(err.message);
      this.router.navigate(['/register']);
    });
  }

  getUserName(): string | null {
    return localStorage.getItem('userName');
  }

  logout() {
    void this.signOut().catch(err => alert(err.message));
  }

  /** Awaitable sign-out for workspaces that display pending/error states. */
  async signOut(): Promise<void> {
    await this.afAuth.signOut();
    this.loggedIn.next(false);
    this.userName.next(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    const navigated = await this.router.navigate(['/login'], {replaceUrl: true});
    if (!navigated) throw new Error('Signed out. Open the login page to continue.');
  }

  isLoggedIn(): boolean {
    return this.loggedIn.value;
  }

  private async updateLastLogin(uid: string) {
    try {
      await this.afs.doc(`users/${uid}`).update(
        { lastLogin: firebase.firestore.FieldValue.serverTimestamp() }
      );
    } catch (e) {
      console.warn('lastLogin update skipped:', e);
    }
  }

}
